import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import api, { fileUrl } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowLeft, Camera, MessageSquare, CheckCircle2, RotateCcw, User, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";

export default function TenantRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [reopenText, setReopenText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get(`/tenant/work-orders/${id}`).then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, [id]);

  if (data === null) return <DashboardLayout nav={getNav("tenant")} title="Request"><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div></DashboardLayout>;
  if (!data) return <DashboardLayout nav={getNav("tenant")} title="Request"><p>Not found.</p></DashboardLayout>;

  const { work_order: w, photos, notes } = data;
  const completed = w.status === "Completed";

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("work_order_id", id); fd.append("phase", "tenant"); fd.append("description", "Tenant upload");
      await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded"); load();
    } catch (err) { toast.error("Upload failed"); }
    setBusy(false);
  };
  const addNote = async () => { if (!note.trim()) return; try { await api.post(`/tenant/work-orders/${id}/note`, { text: note }); setNote(""); toast.success("Added"); load(); } catch (e) { toast.error("Failed"); } };
  const confirmResolved = async () => { try { await api.post(`/tenant/work-orders/${id}/confirm-resolved`); toast.success("Thanks for confirming!"); load(); } catch (e) { toast.error("Failed"); } };
  const reopen = async () => { if (!reopenText.trim()) return toast.error("Please describe the issue"); try { await api.post(`/tenant/work-orders/${id}/reopen`, { text: reopenText }); setReopenText(""); toast.success("Request reopened"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <DashboardLayout nav={getNav("tenant")} title={w.wo_number}>
      <button onClick={() => navigate("/app/tenant")} className="flex items-center gap-1 text-sm text-slate-500 mb-4 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="tenant-req-detail">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2"><h2 className="font-head text-xl font-semibold text-slate-900">{w.service_category}</h2><UrgencyBadge urgency={w.urgency} /></div>
            <p className="text-slate-600 mt-1">{w.problem_description}</p>
          </div>
          <StatusBadge status={w.status} />
        </div>
        <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
          {w.scheduled_date && <div><div className="text-xs uppercase font-bold text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> Scheduled</div><div className="mt-1 text-slate-800">{w.scheduled_date}</div></div>}
          {w.contractor && <div><div className="text-xs uppercase font-bold text-slate-400 flex items-center gap-1"><User className="w-3 h-3" /> Contractor</div><div className="mt-1 text-slate-800">{w.contractor.first_name || w.contractor.company_name || "Assigned"}</div></div>}
          {w.preferred_windows?.length > 0 && <div><div className="text-xs uppercase font-bold text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Preferred</div><div className="mt-1 text-slate-800">{w.preferred_windows.join(", ")}</div></div>}
        </div>
        {(w.timeline || []).length > 0 && (
          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="text-xs uppercase font-bold text-slate-400 mb-2">Status History</div>
            <div className="space-y-1">{w.timeline.map((t, i) => <div key={i} className="text-sm text-slate-600 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />{t.status} <span className="text-xs text-slate-400">· {new Date(t.at).toLocaleString()}</span></div>)}</div>
          </div>
        )}
      </div>

      {completed && !w.tenant_confirmed_resolved && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-emerald-800">This request is marked complete. Is the issue resolved?</p>
          <Button data-testid="tenant-confirm-btn" onClick={confirmResolved} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm Resolved</Button>
        </div>
      )}
      {w.tenant_confirmed_resolved && <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="tenant-confirmed"><CheckCircle2 className="w-4 h-4" /> You confirmed this issue as resolved.</div>}

      {completed && (
        <div className="mt-4 bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Issue not fully resolved?</div>
          <Textarea data-testid="tenant-reopen-text" value={reopenText} onChange={(e) => setReopenText(e.target.value)} rows={2} placeholder="Describe what still needs attention…" />
          <Button data-testid="tenant-reopen-btn" onClick={reopen} variant="outline" className="mt-2 text-amber-600 border-amber-200">Reopen / Request Follow-up</Button>
        </div>
      )}

      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><Camera className="w-4 h-4" /> Photos ({photos.length})</h3>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="tenant-photo-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="tenant-photo-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Photo"}</Button>
        </div>
        {photos.length === 0 ? <p className="text-sm text-slate-400">No photos yet.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {photos.map((ph) => <div key={ph.id} className="rounded-lg overflow-hidden border border-slate-200"><img src={fileUrl(ph.storage_path)} alt={ph.phase} className="w-full h-32 object-cover" /></div>)}
          </div>
        )}
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-head font-semibold text-slate-900 mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Updates & Follow-up</h3>
        <div className="space-y-2 mb-3">
          {notes.length === 0 && <p className="text-sm text-slate-400">No updates yet.</p>}
          {notes.map((n) => <div key={n.id} className="bg-slate-50 rounded-lg p-3 text-sm"><div className="font-medium text-slate-800">{n.author_name}</div><p className="text-slate-600">{n.text}</p></div>)}
        </div>
        <Textarea data-testid="tenant-note-text" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add follow-up information…" />
        <Button data-testid="tenant-note-btn" size="sm" onClick={addNote} className="mt-2 bg-blue-600 hover:bg-blue-700">Add Update</Button>
      </div>
    </DashboardLayout>
  );
}
